import React from 'react';
import { Alert, Container, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';

function isChunkLoadError(error) {
  return (
    error?.name === 'ChunkLoadError' ||
    /loading chunk .+ failed/i.test(error?.message) ||
    /failed to fetch dynamically imported module/i.test(error?.message)
  );
}

// Mindestabstand zwischen zwei automatischen Chunk-Reloads. Verhindert eine
// Endlosschleife: Ein rein "beim Mount löschen"-Ansatz hebt das Flag bei JEDEM
// Mount der ErrorBoundary auf (auch direkt nach dem durch sie selbst
// ausgelösten Reload) und erlaubt damit sofort den nächsten automatischen
// Reload — in der Praxis beobachtet mit hunderten Reloads in wenigen Sekunden,
// sobald der Chunk dauerhaft nicht ladbar ist (z.B. weil der Server für
// fehlende /static/*-Dateien fälschlich den SPA-HTML-Fallback liefert statt
// 404). Stattdessen wird der Zeitpunkt des letzten Reload-Versuchs gespeichert
// und ein erneuter Auto-Reload nur zugelassen, wenn genug Zeit vergangen ist.
const CHUNK_RELOAD_COOLDOWN_MS = 30000;

function canAttemptChunkReload() {
  // sessionStorage kann in bestimmten Umgebungen werfen (z.B. Capacitor-
  // WebView mit deaktiviertem Storage, Safari/Chrome Private-Mode mit vollem
  // Quota). Ein Wurf hier läuft in der obersten ErrorBoundary selbst und
  // würde, unbehandelt, den kompletten React-Baum unmounten — daher defensiv
  // abgesichert. Im Fehlerfall lieber KEIN Auto-Reload (normale Fehler-UI mit
  // manuellem Reload-Button) als eine weitere Exception zu riskieren.
  try {
    const lastAttempt = sessionStorage.getItem('chunk_reload_attempted');
    if (!lastAttempt) return true;
    const elapsed = Date.now() - Number(lastAttempt);
    return Number.isNaN(elapsed) || elapsed > CHUNK_RELOAD_COOLDOWN_MS;
  } catch (storageError) {
    return false;
  }
}

function markChunkReloadAttempted() {
  try {
    sessionStorage.setItem('chunk_reload_attempted', String(Date.now()));
  } catch (storageError) {
    // Siehe canAttemptChunkReload: Storage-Zugriff kann in manchen Umgebungen
    // scheitern. Der Reload wird trotzdem versucht, nur das Cooldown-Tracking
    // für den nächsten Fehler entfällt dann (Best-Effort).
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.headingRef = React.createRef();
  }

  static getDerivedStateFromError(error) {
    // React-Contract: getDerivedStateFromError muss eine reine Funktion sein
    // (keine Seiteneffekte wie sessionStorage.setItem oder setTimeout) — und
    // zusätzlich MUSS hier immer hasError:true zurückgegeben werden.
    //
    // Grund (gegen den react-dom-Quellcode verifiziert): Gibt diese Methode
    // hasError:false zurück, versucht React einen Recovery-Render der
    // werfenden Children, BEVOR componentDidCatch überhaupt läuft. Bei einem
    // persistenten Fehler (z.B. ein dauerhaft nicht ladbarer Chunk) wirft der
    // Teilbaum im selben Commit sofort erneut — die Boundary fängt das nicht
    // nochmal ab, componentDidCatch (und damit der Auto-Reload) wird für
    // diesen Fehler nie committet. Der Auto-Reload würde also ausgerechnet im
    // Zielszenario (persistenter ChunkLoadError) versagen. Daher: immer
    // Fallback-UI rendern, nie einen Recovery-Render der kaputten Children
    // versuchen. Der Auto-Reload-Seiteneffekt läuft stattdessen in
    // componentDidCatch (siehe dort).
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error, errorInfo) {
    // Fatale Render-Fehler an der obersten Fehlergrenze müssen auch in Production
    // sichtbar bleiben (kein Monitoring-Backend) — daher bewusst direktes console.error,
    // das NICHT durch die Logger-Abstraktion stumm geschaltet wird.
    // Die aktuelle Route wird mitgeloggt, damit Produktionsvorfälle (bei denen
    // z.B. die URL bereits auf eine Folgeseite zeigt, die App aber wegen des
    // Fehlers nie dort ankommt) im Nachhinein diagnostizierbar sind.
    // eslint-disable-next-line no-console
    console.error('Error caught by boundary:', error, errorInfo, 'route:', window.location.href);
    this.setState({ errorInfo });

    if (isChunkLoadError(error) && canAttemptChunkReload()) {
      // Die Chunk-Fehler-UI ("Laden der Seite fehlgeschlagen…") kann hier kurz
      // vor dem Reload sichtbar aufblitzen — das ist gewollt (sichtbares
      // Feedback statt weißem Flackern) und dem Recovery-Render-Problem aus
      // getDerivedStateFromError geschuldet: die Fallback-UI wird IMMER erst
      // committet, der Reload folgt danach als Seiteneffekt.
      markChunkReloadAttempted();
      setTimeout(() => window.location.reload(), 0);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.hasError && this.state.hasError && this.headingRef.current) {
      this.headingRef.current.focus();
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    const segments = window.location.pathname.split('/').filter(Boolean);
    const tenantSlug = segments[0] || 'mvg';
    window.location.href = `/${tenantSlug}/dashboard`;
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = isChunkLoadError(this.state.error);

      return (
        <Container className="mt-5" id="error-boundary-container">
          <Alert
            variant="danger"
            id="error-boundary-alert"
            role="alert"
          >
            <Alert.Heading
              id="error-boundary-heading"
              as="h1"
              tabIndex={-1}
              ref={this.headingRef}
            >
              {isChunkError
                ? 'Laden der Seite fehlgeschlagen'
                : 'Etwas ist schief gelaufen'}
            </Alert.Heading>
            <p id="error-boundary-message">
              {isChunkError
                ? 'Die Seite konnte nicht geladen werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.'
                : 'Die Anwendung ist auf einen unerwarteten Fehler gestoßen.'}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <>
                <hr />
                <details id="error-boundary-details">
                  <summary>Fehlerdetails (nur in Entwicklungsumgebung)</summary>
                  <pre className="mt-3" id="error-boundary-stack">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              </>
            )}

            <hr />
            <div className="d-flex gap-2">
              <Button
                id="error-boundary-reset-button"
                variant="primary"
                onClick={this.handleReset}
              >
                Zur Startseite zurückkehren
              </Button>
              <Button
                id="error-boundary-reload-button"
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Seite neu laden
              </Button>
            </div>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

export default ErrorBoundary;
